/**
 * Mise en forme des formations de jugement.
 *
 * Les métadonnées officielles (Judilibre, bases publiques) renvoient la
 * formation sous une forme technique : « CHAMBRE_SOCIALE », « SOC »,
 * « ASSEMBLEE_PLENIERE »… Affichée telle quelle, elle produisait des libellés
 * du type « Cour de cassation, CHAMBRE_SOCIALE, 12 mars 2024 ».
 * Source unique de vérité : tout point d'affichage passe par ces fonctions.
 */

/** Formations connues, sous leur forme longue (énumération) et courte (code). */
const FORMATION_LABELS: Record<string, string> = {
  // Formes longues
  CHAMBRE_SOCIALE: "chambre sociale",
  CHAMBRE_COMMERCIALE: "chambre commerciale",
  CHAMBRE_COMMERCIALE_FINANCIERE_ET_ECONOMIQUE: "chambre commerciale",
  CHAMBRE_CRIMINELLE: "chambre criminelle",
  CHAMBRE_MIXTE: "chambre mixte",
  CHAMBRES_REUNIES: "chambres réunies",
  PREMIERE_CHAMBRE_CIVILE: "première chambre civile",
  DEUXIEME_CHAMBRE_CIVILE: "deuxième chambre civile",
  TROISIEME_CHAMBRE_CIVILE: "troisième chambre civile",
  CHAMBRE_CIVILE_1: "première chambre civile",
  CHAMBRE_CIVILE_2: "deuxième chambre civile",
  CHAMBRE_CIVILE_3: "troisième chambre civile",
  ASSEMBLEE_PLENIERE: "Assemblée plénière",
  FORMATION_PLENIERE: "formation plénière",
  FORMATION_RESTREINTE: "formation restreinte",
  SECTION_DU_CONTENTIEUX: "section du contentieux",
  // Codes courts
  SOC: "chambre sociale",
  COMM: "chambre commerciale",
  CRIM: "chambre criminelle",
  CIV1: "première chambre civile",
  CIV2: "deuxième chambre civile",
  CIV3: "troisième chambre civile",
  MIXTE: "chambre mixte",
  AP: "Assemblée plénière",
  CR: "chambres réunies",
};

/** `true` si la valeur ressemble à une énumération technique (pas du français). */
function looksLikeEnum(value: string): boolean {
  return /^[A-Z0-9]+(?:_[A-Z0-9]+)*$/.test(value) && !/^[IVXLC]+$/.test(value);
}

/**
 * Convertit une formation technique en libellé français lisible.
 * Une valeur déjà lisible (« chambre sociale ») est renvoyée inchangée.
 */
export function formatFormation(value: string): string {
  const raw = (value ?? "").trim();
  if (!raw) return raw;
  const key = raw.replace(/[\s-]+/g, "_").toUpperCase();
  const known = FORMATION_LABELS[key];
  if (known) return known;
  if (!looksLikeEnum(raw)) return raw;
  // Inconnue mais visiblement technique : on la rend au moins lisible.
  return raw.toLowerCase().replace(/_/g, " ").replace(/\s{2,}/g, " ").trim();
}

/**
 * Applique `formatFormation` à chaque segment d'un libellé de juridiction
 * (« Cour de cassation, CHAMBRE_SOCIALE, 12 mars 2024 »).
 */
export function formatCourtLabel(label: string): string {
  const raw = (label ?? "").trim();
  if (!raw) return raw;
  return raw
    .split(",")
    .map((part) => {
      const segment = part.trim();
      if (!segment) return segment;
      return formatFormation(segment);
    })
    .filter(Boolean)
    .join(", ");
}
